// --- External library imports ---
import React, {useEffect, useRef, useState} from "react";

// --- Context imports ---
import {useAuthForm} from '@/context/AuthFormContext';
import {useAuth} from '@/context/AuthContext';
import {useJobSearch} from '@/context/JobSearchContext';

// --- API imports ---
import {JobApplication} from "@/api/jobApplicationApi";

// --- UI Component imports ---
import {Label} from "@/components/ui/label";
import {Textarea} from "@/components/ui/textarea";
import {Progress} from "@/components/ui/progress";
import {Button} from "@/components/ui/button";

// --- Popup Components imports ---
import {CheckoutErrorPopup, JobPreferencesPopup, PrivacyPolicyPopup, TermsOfUsePopup} from "@/components/popups";

// --- Icons imports ---
import {
    Award,
    Briefcase,
    Calendar,
    ChevronDown,
    ChevronRight,
    FileText,
    MapPin,
    MessageCircle,
    Search,
    Shield,
    Upload,
    Wifi,
    X
} from "lucide-react";

// --- Autocomplete Components imports ---
import LocationAutocomplete from "@/components/forms/LocationAutocomplete";
import JobTitleAutocomplete from "@/components/forms/JobTitleAutocomplete";

// --- Data imports ---
import {packages} from "../landing/PricingSection";
import {Link} from "react-router-dom";
import {SearchApi} from "@/api/searchStatusApi.js";

// --- Constants ---
const MAX_FILE_SIZE_MB = 1;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
];

/**
 * JobSearchForm - Main form component that handles job search submission
 * All steps combined into a single form interface
 */
export default function JobSearchForm({
                                          onComplete,
                                          submissionError: externalSubmissionError,
                                          isSubmitting: externalIsSubmitting,
                                          selectedPackage: externalSelectedPackage,
                                          onPackageChange: externalOnPackageChange
                                      }) {
    // --- State hooks ---
    // Form data
    const [formData, setFormData] = useState({
        job_title: "",
        location: "",
        employment_type: "",
        seniority_levels: "",
        job_preferences: "",
        resume_file: null,
        resume_text: "",
        resume_file_url: "",
        email: "",
        remote_mode: "",
        time_recency: "",
        // Dropdown visibility states
        showEmploymentType: false,
        showSeniorityLevels: false,
        showRemoteMode: false,
        showTimeRecency: false,
        showPackage: false,
    });

    // Selected package state (either internal or from props)
    const [selectedPackage, setSelectedPackage] = useState("free");

    // Authentication state from global context
    const { isSignedIn, userEmail, handleGoogleSignIn } = useAuth();

    // Validation and submission state
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);

    // File upload state
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Popup states
    const [showTermsPopup, setShowTermsPopup] = useState(false);
    const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
    const [showJobPreferencesPopup, setShowJobPreferencesPopup] = useState(false);

    // Lemon Squeezy checkout states
    const [showCheckoutErrorPopup, setShowCheckoutErrorPopup] = useState(false);
    const [showLemonCheckoutShadow, setShowLemonCheckoutShadow] = useState(false);
    const [hasSuccessfulCheckout, setHasSuccessfulCheckout] = useState(false);

    // --- Context hooks ---
    const {
        formData: contextFormData,
        setFormData: setContextFormData,
        isSubmitting: contextIsSubmitting,
        setIsSubmitting: setContextIsSubmitting,
        setSubmissionError: setContextSetSubmissionError
    } = useAuthForm();
    const {setLastSearch} = useJobSearch();

    // --- Refs ---
    const fileInputRef = useRef(null);
    const formContainerRef = useRef(null);

    // Employment Type dropdown refs
    const employmentTypeRef = useRef(null);
    const employmentTypeButtonRef = useRef(null);

    // Seniority Levels dropdown refs
    const seniorityLevelsRef = useRef(null);
    const seniorityLevelsButtonRef = useRef(null);

    // Remote Mode dropdown refs
    const remoteModeRef = useRef(null);
    const remoteModeButtonRef = useRef(null);

    // Time Recency dropdown refs
    const timeRecencyRef = useRef(null);
    const timeRecencyButtonRef = useRef(null);

    // Package dropdown refs
    const packagesRef = useRef(null);
    const packagesButtonRef = useRef(null);

    // --- Update external package state if provided ---
    useEffect(() => {
        if (externalOnPackageChange) {
            externalOnPackageChange(selectedPackage);
        }
    }, [selectedPackage, externalOnPackageChange]);

    // --- Update selected package when external package changes ---
    useEffect(() => {
        if (externalSelectedPackage) {
            setSelectedPackage(externalSelectedPackage);
        }
    }, [externalSelectedPackage]);

    // --- Check if user is already signed in from context ---
    useEffect(() => {
        if (isSignedIn && userEmail) {
            setFormData(prev => ({
                ...prev,
                email: userEmail
            }));
        }
    }, [isSignedIn, userEmail]);

    // --- Lemon.js is initialized ---
    useEffect(() => {
        window.createLemonSqueezy();
    }, []);

    // --- Setup LemonSqueezy global event handler (fires once on mount) ---
    useEffect(() => {
        LemonSqueezy.Setup({
            eventHandler: async (event) => {
                if (event.event === "Checkout.Success") {
                    console.log("✅ Payment was successful");
                    setHasSuccessfulCheckout(true); // Flag to confirm user paid
                }

                if (event === "close") {
                    console.log("❌ Checkout modal was closed manually");
                    setShowLemonCheckoutShadow(false);
                }
            }
        });
    }, []);

    // --- Listen for redirect triggered after LemonSqueezy modal success (via "View Matches" button) ---
    useEffect(() => {
        const handlePostCheckoutRedirect = async () => {
            const hash = window.location.hash;
            const params = new URLSearchParams(hash.split("?")[1]);
            const source = params.get("from");

            const isRedirectToJobResults = hash.startsWith("#job-results");

            const isRedirectedFromCheckout = isRedirectToJobResults && source === "checkout";

            console.log('isRedirectedFromCheckout ', isRedirectedFromCheckout);
            console.log('hasSuccessfulCheckout ', hasSuccessfulCheckout);
            if (isRedirectedFromCheckout && hasSuccessfulCheckout) {
                console.log("🔓 Verified: redirected from LS modal + actual payment");

                // Try closing the LemonSqueezy modal (if it's still open)
                try {
                    console.log("🟢 Closing LemonSqueezy overlay...");
                    LemonSqueezy.Url.Close();
                } catch (err) {
                    console.error("❌ Error closing LemonSqueezy overlay:", err);
                }

                // Remove the iframe manually if it still exists
                const lemonFrame = document.querySelector('iframe[src*="lemonsqueezy.com"]');
                if (lemonFrame) {
                    lemonFrame.remove();
                    console.log("🧹 Removed LemonSqueezy iframe manually.");
                }

                // Restore normal body scrolling
                document.body.classList.remove("lemon-modal-open");
                document.body.style.overflow = "auto";

                setShowLemonCheckoutShadow(false);

                // Continue with job results logic
                await fetchJobResultsStatus();
                resetJobSearchForm();
                scrollToJobResultsSection();
            }

            if (isRedirectToJobResults) {
                scrollToJobResultsSection();
            }
        };

        window.addEventListener("hashchange", handlePostCheckoutRedirect);
        handlePostCheckoutRedirect(); // Trigger once on mount

        return () => window.removeEventListener("hashchange", handlePostCheckoutRedirect);
    }, [hasSuccessfulCheckout]);

    // --- Close dropdowns when clicking outside ---
    useEffect(() => {
        function handleClickOutside(event) {
            // Check employment type dropdown
            if (formData.showEmploymentType &&
                employmentTypeRef.current &&
                !employmentTypeRef.current.contains(event.target) &&
                employmentTypeButtonRef.current &&
                !employmentTypeButtonRef.current.contains(event.target)) {
                setFormData(prev => ({...prev, showEmploymentType: false}));
            }

            // Check seniority levels dropdown
            if (formData.showSeniorityLevels &&
                seniorityLevelsRef.current &&
                !seniorityLevelsRef.current.contains(event.target) &&
                seniorityLevelsButtonRef.current &&
                !seniorityLevelsButtonRef.current.contains(event.target)) {
                setFormData(prev => ({...prev, showSeniorityLevels: false}));
            }

            // Check remote mode dropdown
            if (formData.showRemoteMode &&
                remoteModeRef.current &&
                !remoteModeRef.current.contains(event.target) &&
                remoteModeButtonRef.current &&
                !remoteModeButtonRef.current.contains(event.target)) {
                setFormData(prev => ({...prev, showRemoteMode: false}));
            }

            // Check time recency dropdown
            if (formData.showTimeRecency &&
                timeRecencyRef.current &&
                !timeRecencyRef.current.contains(event.target) &&
                timeRecencyButtonRef.current &&
                !timeRecencyButtonRef.current.contains(event.target)) {
                setFormData(prev => ({...prev, showTimeRecency: false}));
            }

            // Check package dropdown
            if (formData.showPackage &&
                packagesRef.current &&
                !packagesRef.current.contains(event.target) &&
                packagesButtonRef.current &&
                !packagesButtonRef.current.contains(event.target)) {
                setFormData(prev => ({...prev, showPackage: false}));
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [formData]);

    // --- Helper functions ---

    /**
     * Fetches the latest job search status from the server
     * and updates the job search context accordingly.
     */
    const fetchJobResultsStatus = async () => {
        try {
            const status = await SearchApi.getStatus();
            setLastSearch(status);
        } catch (error) {
            console.error('❌ Error fetching search status:', error);
            setLastSearch({task_status: 'failed'});
        }
    };

    /**
     * Resets all job search form fields to their default values.
     * Keeps the signed-in email preserved in the form state.
     */
    const resetJobSearchForm = () => {
        setFormData({
            job_title: "",
            location: "",
            employment_type: "",
            seniority_levels: "",
            job_preferences: "",
            resume_file: null,
            resume_text: "",
            resume_file_url: "",
            email: userEmail || "", // preserve signed-in email
            remote_mode: "",
            time_recency: "",
            showEmploymentType: false,
            showSeniorityLevels: false,
            showRemoteMode: false,
            showTimeRecency: false,
            showPackage: false,
        });
    };

    /**
     * Smoothly scrolls to the #job-results section once it appears in the DOM.
     * Waits up to 5 seconds and checks every 100ms.
     */
    const scrollToJobResultsSection = () => {
        const maxWaitTime = 5000;
        const intervalTime = 100;
        let waited = 0;

        const interval = setInterval(() => {
            const element = document.getElementById('job-results');
            if (element) {
                element.scrollIntoView({behavior: 'smooth'});
                clearInterval(interval);
            }
            waited += intervalTime;
            if (waited >= maxWaitTime) {
                clearInterval(interval);
            }
        }, intervalTime);
    };

    /**
     * Toggle dropdown visibility
     */
    const toggleDropdown = (dropdownName) => {
        setFormData(prev => ({
            ...prev,
            showEmploymentType: dropdownName === 'employmentType' ? !prev.showEmploymentType : false,
            showSeniorityLevels: dropdownName === 'seniorityLevels' ? !prev.showSeniorityLevels : false,
            showRemoteMode: dropdownName === 'remoteMode' ? !prev.showRemoteMode : false,
            showTimeRecency: dropdownName === 'timeRecency' ? !prev.showTimeRecency : false,
            showPackage: dropdownName === 'package' ? !prev.showPackage : false
        }));
    };

    /**
     * Validates required form fields
     */
    const validateForm = () => {
        const newErrors = {};

        if (!formData.job_title.trim()) {
            newErrors.job_title = "Job title is required";
        }
        if (!formData.location.trim()) {
            newErrors.location = "Location is required";
        }
        if (!formData.resume_file && !formData.resume_file_url) {
            newErrors.resume = "Resume is required";
        }

        setErrors(newErrors);

        // If there are errors, scroll to the first error field
        if (Object.keys(newErrors).length > 0) {
            const firstErrorElement = document.querySelector('.text-red-500');
            if (firstErrorElement) {
                firstErrorElement.scrollIntoView({behavior: 'smooth', block: 'center'});
            }
        }

        return Object.keys(newErrors).length === 0;
    };

    /**
     * Handles file uploads for resumes
     * @param {File} file - The file to upload
     */
    const handleFileUpload = async (file) => {
        if (!file) return;

        // Validate file size
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setErrors({...errors, resume: `File size must be less than ${MAX_FILE_SIZE_MB}MB`});
            return;
        }

        // Validate file type
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            setErrors({...errors, resume: "Please upload a PDF, DOC, DOCX, or TXT file"});
            return;
        }

        // Start simulated upload progress for UI feedback
        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Simulate progress while validating
            const progressInterval = setInterval(() => {
                setUploadProgress((prev) => {
                    if (prev >= 90) return prev;
                    return Math.min(prev + 10, 90);
                });
            }, 300);

            // Store the file in state but don't actually upload it yet
            setTimeout(() => {
                clearInterval(progressInterval);
                setUploadProgress(100);

                // Update form data with file info - but no URL yet since we're not uploading
                setFormData((prev) => ({
                    ...prev,
                    resume_file: file,
                    // Not setting resume_file_url since we'll upload during form submission
                }));

                // Clear any resume errors
                const newErrors = {...errors};
                delete newErrors.resume;
                setErrors(newErrors);
            }, 500);
        } catch (err) {
            console.error(err);
            setErrors({...errors, resume: "Failed to process resume. Please try again."});
            setUploadProgress(0);
        } finally {
            // Keep uploading state briefly to show progress
            setTimeout(() => {
                setIsUploading(false);
            }, 500);
        }
    };

    /**
     * Handles file drop events
     */
    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        handleFileUpload(file);
    };

    /**
     * Prevents default behavior for drag over events
     */
    const handleDragOver = (e) => {
        e.preventDefault();
    };

    /**
     * Creates a checkout URL for redirecting to payment
     */
    const createCheckoutUrl = (baseUrl, id, email) => {
        const url = new URL(baseUrl);
        url.searchParams.set("embed", "1");
        url.searchParams.set("logo", "0");
        url.searchParams.set("checkout[email]", email || "");
        url.searchParams.set("checkout[custom][id]", id);
        return url.toString();
    };

    /**
     * Updates job preferences from popup
     */
    const handleJobPreferencesUpdate = (newPreferences) => {
        setFormData(prev => ({
            ...prev,
            job_preferences: newPreferences
        }));
    };

    /**
     * Main form submission handler
     */
    const handleSubmit = async (emailToOveride = null) => {
        // No need to validate here as we already validated in handleMainButtonClick
        const email = emailToOveride || userEmail;

        setIsSubmitting(true);
        setSubmissionError(null);

        try {
            // Fix empty fields for enum values
            const fixEmptyFields = (value) => value === "" ? null : value;

            // Format the data for submission
            const formattedData = {
                job_title: formData.job_title || "Not specified",
                location: formData.location || "Not specified",
                employment_type: fixEmptyFields(formData.employment_type),
                seniority_levels: fixEmptyFields(formData.seniority_levels),
                job_preferences: formData.job_preferences,
                resume_file_url: formData.resume_file_url || null, // Could be null now if uploading with form
                email: email,
                remote_mode: fixEmptyFields(formData.remote_mode),
                time_recency: fixEmptyFields(formData.time_recency),
                selected_package: selectedPackage,
                status: "pending",
                is_google_auth: true
            };

            // Handle parent completion callback
            if (onComplete) {
                onComplete(formattedData);
                return;
            }

            // Create job application in backend - now passing the resume file if present
            const jobApplicationData = await JobApplication.create(
                formattedData,
                formData.resume_file // Pass the file to be uploaded as part of the submission
            );

            const id = jobApplicationData?.id;

            if (!id) {
                throw new Error("Missing ID in response from JobApplication.create()");
            }

            // Handle free vs paid packages
            if (selectedPackage === "free") {
                await fetchJobResultsStatus();
                resetJobSearchForm();
                scrollToJobResultsSection();
            } else {
                // Redirect to payment page for paid packages
                const checkoutUrl = createCheckoutUrl(
                    packages[selectedPackage].checkoutUrl,
                    id,
                    email
                );

                try {
                    setShowLemonCheckoutShadow(true);
                    console.log("✅ Opening LemonSqueezy modal using new API");
                    LemonSqueezy.Url.Open(checkoutUrl);
                } catch (err) {
                    console.error("❌ Failed to open LemonSqueezy overlay:", err);
                    setShowCheckoutErrorPopup(true);
                    setShowLemonCheckoutShadow(false);
                }
            }
        } catch (error) {
            console.error("Submission error:", error);
            setSubmissionError(error.message || "There was an error processing your request. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Handles Google Sign In authentication
     */
    const handleSignIn = async () => {
        try {
            setIsSubmitting(true);
            setContextIsSubmitting?.(true);

            const { success, error, user } = await handleGoogleSignIn();

            if (!success || error) {
                setSubmissionError("Google Sign In failed. Please try again.");
                setContextSetSubmissionError?.("Google Sign In failed. Please try again.");
                return;
            }

            // Submit the form immediately after sign in
            // No need to validate again as we already did before sign in
            await handleSubmit(user.email);
        } catch (err) {
            setSubmissionError("An unexpected error occurred during authentication.");
            setContextSetSubmissionError?.("An unexpected error occurred during authentication.");
        } finally {
            setIsSubmitting(false);
            setContextIsSubmitting?.(false);
        }
    };

    /**
     * Main action button handler - handles both sign in and form submission
     */
    const handleMainButtonClick = async () => {
        // Always validate required fields first
        if (!validateForm()) {
            return; // Stop if validation fails
        }

        if (!isSignedIn) {
            await handleSignIn();
        } else {
            await handleSubmit();
        }
    };

    /**
     * Gets the text for main action button based on sign in status and package
     */
    const getButtonText = () => {
        if (selectedPackage === "free") {
            return isSignedIn ? "Get Your Free Matches" : "Sign in & Try for Free";
        }
        return isSignedIn ? "Proceed to Payment" : "Sign in & Proceed to Payment";
    };

    const renderContent = () => {
        return (<div className="form-container overflow-y-auto px-1" ref={formContainerRef}>
            <div className="space-y-6">
                <div className="space-y-2">
                    <h2 className="text-[23px] md:text-2xl font-bold text-gray-900">Find Your Perfect Job
                        Match!</h2>
                    <p className="text-gray-600 text-lg">Upload your resume and let our AI do the hard work</p>
                </div>

                {/* --- Main fields section --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* --- Job Title input field --- */}
                    <div>
                        <Label htmlFor="job_title"
                               className="text-sm font-semibold text-gray-800 flex items-center mb-2">
                            <Search className="w-4 h-4 mr-2 text-blue-600"/>
                            Job Title
                            {errors.job_title && (
                                <span className="ml-1 text-red-500 font-normal">(required)</span>
                            )}
                        </Label>

                        <JobTitleAutocomplete
                            value={formData.job_title}
                            onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                            onSelect={(val) => setFormData({...formData, job_title: val})}
                            placeholder="e.g. Software Engineer"
                            className={`mt-1 w-full`}
                            error={!!errors.job_title}
                        />
                    </div>

                    {/* --- Location input field --- */}
                    <div>
                        <Label htmlFor="location"
                               className="text-sm font-semibold text-gray-800 flex items-center mb-2">
                            <MapPin className="w-4 h-4 mr-2 text-blue-600"/>
                            Location
                            {errors.location && (
                                <span className="ml-1 text-red-500 font-normal">(required)</span>
                            )}
                        </Label>
                        <LocationAutocomplete
                            value={formData.location}
                            onChange={(e) => setFormData({...formData, location: e.target.value})}
                            onSelect={(val) => setFormData({...formData, location: val})}
                            placeholder="e.g. New York, NY"
                            className={`mt-1`}
                            error={!!errors.location}
                        />
                    </div>
                </div>

                {/* --- Optional dropdown fields --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* --- Employment Type dropdown --- */}
                    <div>
                        <Label className="text-sm font-semibold text-gray-800 flex items-center mb-2">
                            <Briefcase className="w-4 h-4 mr-2 text-blue-600"/>
                            Employment Type (Optional)
                        </Label>
                        <div className="relative mt-1">
                            <button
                                type="button"
                                ref={employmentTypeButtonRef}
                                onClick={() => toggleDropdown('employmentType')}
                                className="w-full h-12 bg-[#e9f2ff] border border-blue-200 text-gray-800 hover:border-blue-400 focus:border-blue-500 focus:ring-blue-100 rounded-lg shadow-sm text-base px-3 transition-all flex items-center justify-between"
                            >
                                <span className={formData.employment_type ? "text-gray-800" : "text-gray-500"}>
                                    {formData.employment_type === "full_time" && "Full time"}
                                    {formData.employment_type === "part_time" && "Part time"}
                                    {formData.employment_type === "contract" && "Contract"}
                                    {formData.employment_type === "temporary" && "Temporary"}
                                    {formData.employment_type === "volunteer" && "Volunteer"}
                                    {!formData.employment_type && "Any"}
                                </span>
                                <ChevronDown className="h-5 w-5 text-gray-500"/>
                            </button>

                            {formData.showEmploymentType && (
                                <div
                                    ref={employmentTypeRef}
                                    className="absolute w-full z-50 mt-1 bg-white rounded-lg border shadow-md py-1 max-h-60 overflow-auto"
                                >
                                    <div className="p-2">
                                        {[
                                            {id: "", label: "Any"},
                                            {id: "full_time", label: "Full time"},
                                            {id: "part_time", label: "Part time"},
                                            {id: "contract", label: "Contract"},
                                            {id: "temporary", label: "Temporary"},
                                            {id: "volunteer", label: "Volunteer"},
                                        ].map((type) => (
                                            <div
                                                key={type.id}
                                                className="flex items-center space-x-2 p-2 hover:bg-blue-50 rounded cursor-pointer"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        employment_type: type.id,
                                                        showEmploymentType: false
                                                    }));
                                                }}
                                            >
                                                <div
                                                    className={`w-4 h-4 border rounded-full flex items-center justify-center ${formData.employment_type === type.id ? "border-[#3B5CCC]" : "border-blue-200"}`}>
                                                    {formData.employment_type === type.id && (
                                                        <div className="w-2 h-2 rounded-full bg-[#3B5CCC]"></div>
                                                    )}
                                                </div>
                                                <span>{type.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* --- Remote Work Mode dropdown --- */}
                    <div>
                        <Label className="text-sm font-semibold text-gray-800 flex items-center mb-2">
                            <Wifi className="w-4 h-4 mr-2 text-blue-600"/>
                            Remote Work Mode (Optional)
                        </Label>
                        <div className="relative mt-1">
                            <button
                                type="button"
                                ref={remoteModeButtonRef}
                                onClick={() => toggleDropdown('remoteMode')}
                                className="w-full h-12 bg-[#e9f2ff] border border-blue-200 text-gray-800 hover:border-blue-400 focus:border-blue-500 focus:ring-blue-100 rounded-lg shadow-sm text-base px-3 transition-all flex items-center justify-between"
                            >
                                <span className={formData.remote_mode ? "text-gray-800" : "text-gray-500"}>
                                    {formData.remote_mode === "at_work" && "On-site"}
                                    {formData.remote_mode === "remote" && "Remote"}
                                    {formData.remote_mode === "hybrid" && "Hybrid"}
                                    {!formData.remote_mode && "Any"}
                                </span>
                                <ChevronDown className="h-5 w-5 text-gray-500"/>
                            </button>

                            {formData.showRemoteMode && (
                                <div
                                    ref={remoteModeRef}
                                    className="absolute w-full z-50 mt-1 bg-white rounded-lg border shadow-md py-1 max-h-60 overflow-auto"
                                >
                                    <div className="p-2">
                                        {[
                                            {id: "", label: "Any"},
                                            {id: "at_work", label: "On-site"},
                                            {id: "remote", label: "Remote"},
                                            {id: "hybrid", label: "Hybrid"}
                                        ].map((mode) => (
                                            <div
                                                key={mode.id}
                                                className="flex items-center space-x-2 p-2 hover:bg-blue-50 rounded cursor-pointer"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        remote_mode: mode.id,
                                                        showRemoteMode: false
                                                    }));
                                                }}
                                            >
                                                <div
                                                    className={`w-4 h-4 border rounded-full flex items-center justify-center ${formData.remote_mode === mode.id ? "border-[#3B5CCC]" : "border-blue-200"}`}>
                                                    {formData.remote_mode === mode.id && (
                                                        <div className="w-2 h-2 rounded-full bg-[#3B5CCC]"></div>
                                                    )}
                                                </div>
                                                <span>{mode.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- Second row of dropdowns --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* --- Experience Level dropdown --- */}
                    <div>
                        <Label className="text-sm font-semibold text-gray-800 flex items-center mb-2">
                            <Award className="w-4 h-4 mr-2 text-blue-600"/>
                            Experience Level (Optional)
                        </Label>
                        <div className="relative mt-1">
                            <button
                                type="button"
                                ref={seniorityLevelsButtonRef}
                                onClick={() => toggleDropdown('seniorityLevels')}
                                className="w-full h-12 bg-[#e9f2ff] border border-blue-200 text-gray-800 hover:border-blue-400 focus:border-blue-500 focus:ring-blue-100 rounded-lg shadow-sm text-base px-3 transition-all flex items-center justify-between"
                            >
                                <span className={formData.seniority_levels ? "text-gray-800" : "text-gray-500"}>
                                    {formData.seniority_levels === "mid_senior_level" && "Mid Senior Level"}
                                    {formData.seniority_levels === "entry_level" && "Entry Level"}
                                    {formData.seniority_levels === "associate" && "Associate"}
                                    {formData.seniority_levels === "internship" && "Internship"}
                                    {formData.seniority_levels === "director" && "Director"}
                                    {!formData.seniority_levels && "Any"}
                                </span>
                                <ChevronDown className="h-5 w-5 text-gray-500"/>
                            </button>

                            {formData.showSeniorityLevels && (
                                <div
                                    ref={seniorityLevelsRef}
                                    className="absolute w-full z-50 mt-1 bg-white rounded-lg border shadow-md py-1 max-h-60 overflow-auto"
                                >
                                    <div className="p-2">
                                        {[
                                            {id: "", label: "Any"},
                                            {id: "mid_senior_level", label: "Mid Senior Level"},
                                            {id: "entry_level", label: "Entry Level"},
                                            {id: "associate", label: "Associate"},
                                            {id: "internship", label: "Internship"},
                                            {id: "director", label: "Director"},
                                        ].map((level) => (
                                            <div
                                                key={level.id}
                                                className="flex items-center space-x-2 p-2 hover:bg-blue-50 rounded cursor-pointer"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        seniority_levels: level.id,
                                                        showSeniorityLevels: false
                                                    }));
                                                }}
                                            >
                                                <div
                                                    className={`w-4 h-4 border rounded-full flex items-center justify-center ${formData.seniority_levels === level.id ? "border-[#3B5CCC]" : "border-blue-200"}`}>
                                                    {formData.seniority_levels === level.id && (
                                                        <div className="w-2 h-2 rounded-full bg-[#3B5CCC]"></div>
                                                    )}
                                                </div>
                                                <span>{level.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* --- Time Recency dropdown --- */}
                    <div>
                        <Label className="text-sm font-semibold text-gray-800 flex items-center mb-2">
                            <Calendar className="w-4 h-4 mr-2 text-blue-600"/>
                            Time Recency (Optional)
                        </Label>
                        <div className="relative mt-1">
                            <button
                                type="button"
                                ref={timeRecencyButtonRef}
                                onClick={() => toggleDropdown('timeRecency')}
                                className="w-full h-12 bg-[#e9f2ff] border border-blue-200 text-gray-800 hover:border-blue-400 focus:border-blue-500 focus:ring-blue-100 rounded-lg shadow-sm text-base px-3 transition-all flex items-center justify-between"
                            >
                                <span className={formData.time_recency ? "text-gray-800" : "text-gray-500"}>
                                    {formData.time_recency === "day" && "Last 24 hours"}
                                    {formData.time_recency === "week" && "Last week"}
                                    {formData.time_recency === "month" && "Last month"}
                                    {!formData.time_recency && "Any"}
                                </span>
                                <ChevronDown className="h-5 w-5 text-gray-500"/>
                            </button>

                            {formData.showTimeRecency && (
                                <div
                                    ref={timeRecencyRef}
                                    className="absolute w-full z-50 mt-1 bg-white rounded-lg border shadow-md py-1 max-h-60 overflow-auto"
                                >
                                    <div className="p-2">
                                        {[
                                            {id: "", label: "Any"},
                                            {id: "day", label: "Last 24 hours"},
                                            {id: "week", label: "Last week"},
                                            {id: "month", label: "Last month"}
                                        ].map((time) => (
                                            <div
                                                key={time.id}
                                                className="flex items-center space-x-2 p-2 hover:bg-blue-50 rounded cursor-pointer"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        time_recency: time.id,
                                                        showTimeRecency: false
                                                    }));
                                                }}
                                            >
                                                <div
                                                    className={`w-4 h-4 border rounded-full flex items-center justify-center ${formData.time_recency === time.id ? "border-[#3B5CCC]" : "border-blue-200"}`}>
                                                    {formData.time_recency === time.id && (
                                                        <div className="w-2 h-2 rounded-full bg-[#3B5CCC]"></div>
                                                    )}
                                                </div>
                                                <span>{time.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- Upload Resume and Package Selection --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* --- Resume Upload Field --- */}
                    <div>
                        <Label className="text-sm font-semibold text-gray-800 flex items-center mb-2">
                            <FileText className="w-4 h-4 mr-2 text-blue-600"/>
                            Upload Your Resume
                            {errors.resume && (
                                <span className="ml-1 text-red-500 font-normal">(required)</span>
                            )}
                        </Label>

                        {/* --- Handle resume drag and drop area --- */}
                        <div
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            className={`mt-1 border-2 border-dashed rounded-lg group transition-all duration-300 shadow-sm h-12 flex items-center ${
                                errors.resume
                                    ? "border-red-200 bg-red-50"
                                    : "border-blue-200 hover:border-blue-400 hover:bg-blue-50/30 bg-[#e9f2ff]"
                            } cursor-pointer`}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept=".pdf,.docx,.doc,.txt"
                                onChange={(e) => handleFileUpload(e.target.files[0])}
                            />

                            {/* --- Show upload placeholder when no file is present --- */}
                            {!formData.resume_file && !isUploading && (
                                <div className="flex items-center px-3 w-full">
                                    <Upload className="w-5 h-5 text-blue-600 mr-2"/>
                                    {/*<span className="text-grey-800">Drop Resume or Browse</span>*/}
                                    <span className="text-grey-300">Drop or Browse Resume</span>
                                </div>
                            )}

                            {/* --- Display upload progress if file is uploading --- */}
                            {isUploading && (
                                <div className="flex items-center justify-between px-3 w-full">
                                    <div className="flex items-center">
                                        <div
                                            className="animate-spin mr-2 h-5 w-5 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                                        <span className="text-gray-600">Uploading...</span>
                                    </div>
                                    <Progress value={uploadProgress} className="h-2 w-20 bg-blue-100"
                                              indicatorClassName="bg-[#3B5CCC]"/>
                                </div>
                            )}

                            {/* --- Show uploaded file info --- */}
                            {formData.resume_file && !isUploading && (
                                <div className="flex items-center justify-between px-3 w-full">
                                    <div className="flex items-center truncate">
                                        <FileText className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0"/>
                                        <span className="text-gray-800 truncate">{formData.resume_file.name}</span>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-red-100 hover:text-red-600"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFormData({...formData, resume_file: null, resume_file_url: ""});
                                        }}
                                    >
                                        <X className="w-4 h-4"/>
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* --- Package Selection Dropdown --- */}
                    <div>
                        <Label className="text-sm font-semibold text-gray-800 flex items-center mb-2">
                            <Award className="w-4 h-4 mr-2 text-blue-600"/>
                            Package
                        </Label>
                        <div className="relative mt-1">
                            <button
                                type="button"
                                ref={packagesButtonRef}
                                onClick={() => toggleDropdown('package')}
                                className="w-full h-12 bg-[#e9f2ff] border border-blue-200 text-gray-800 hover:border-blue-400 focus:border-blue-500 focus:ring-blue-100 rounded-lg shadow-sm text-base px-3 transition-all flex items-center justify-between"
                            >
                                <span className="text-gray-800">
                                    {selectedPackage === "free" && "Free"}
                                    {selectedPackage === "basic" && "Basic"}
                                    {selectedPackage === "premium" && "Premium"}
                                    {selectedPackage === "professional" && "Elite"}
                                </span>
                                <ChevronDown className="h-5 w-5 text-gray-500"/>
                            </button>

                            {/* --- Package Dropdown --- */}
                            {formData.showPackage && (
                                <div
                                    ref={packagesRef}
                                    className="absolute w-full z-50 mt-1 bg-white rounded-lg border shadow-md overflow-hidden text-xs"
                                >
                                    {[
                                        {
                                            id: "free",
                                            label: "Free",
                                            jobs: "5 Jobs",
                                            accuracy: "Up to 99% Accuracy",
                                            price: "$0"
                                        },
                                        {
                                            id: "basic",
                                            label: "Basic",
                                            jobs: "10 Jobs",
                                            accuracy: "Up to 99% Accuracy",
                                            price: "$9.99"
                                        },
                                        {
                                            id: "premium",
                                            label: "Premium",
                                            jobs: "20 Jobs",
                                            accuracy: "Up to 99% Accuracy",
                                            price: "$19.99"
                                        },
                                        {
                                            id: "professional",
                                            label: "Elite",
                                            jobs: "30 Jobs",
                                            accuracy: "Up to 99% Accuracy",
                                            price: "$29.99"
                                        },
                                    ].map((pkg) => (
                                        <div
                                            key={pkg.id}
                                            className="p-2 hover:bg-gray-50 cursor-pointer border-b last:border-0 border-gray-100 flex items-center"
                                            onClick={() => {
                                                setSelectedPackage(pkg.id);
                                                setFormData(prev => ({...prev, showPackage: false}));
                                            }}
                                        >
                                            <div className="w-20 font-semibold text-gray-800">{pkg.label}</div>
                                            <div className="mx-2 text-gray-400">|</div>
                                            <div className="w-20 text-gray-600">{pkg.jobs}</div>
                                            <div className="mx-2 text-gray-400">|</div>
                                            <div className="flex-grow text-gray-600">{pkg.accuracy}</div>
                                            <div
                                                className="w-14 text-right font-semibold text-gray-800">{pkg.price}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- Render Job Preferences textarea --- */}
                <div>
                    <Label htmlFor="preferences"
                           className="text-sm font-semibold text-gray-800 flex items-center mb-2">
                        <MessageCircle className="w-4 h-4 mr-2 text-blue-600"/>
                        Job Preferences (Critical)

                        {/* --- Open Job Preferences Popup button --- */}
                        <button
                            type="button"
                            onClick={() => setShowJobPreferencesPopup(true)}
                            className="ml-2 text-gray-400 hover:text-blue-600 transition-colors"
                            aria-label="Edit job preferences"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="w-4 h-4"
                            >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                    </Label>
                    <div className="relative">
                        <Textarea
                            id="preferences"
                            placeholder="Help us tailor your job matches by sharing any specific preferences or requirements you have (e.g. certain technologies, growth opportunities, or a particular company culture). The more details you provide, the more accurate your matches will be."
                            value={formData.job_preferences}
                            onChange={(e) => setFormData({...formData, job_preferences: e.target.value})}
                            rows={3}
                            style={{resize: 'none'}}
                            className="mt-1 bg-[#e9f2ff] border border-blue-200 text-gray-800 placeholder-gray-500 hover:border-blue-400 focus:border-blue-500 focus:ring-blue-100 rounded-lg text-base shadow-sm transition-all"
                        />
                    </div>
                </div>

                {/* --- Main action button --- */}
                <Button
                    className={`w-full mt-5 ${
                        selectedPackage === "free"
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-600 hover:bg-blue-700"
                    } h-12 text-base font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group`}
                    onClick={handleMainButtonClick}
                    disabled={isSubmitting || externalIsSubmitting}
                >
                    {(isSubmitting || externalIsSubmitting) ? (
                        <div className="flex items-center justify-center">
                            <div
                                className="animate-spin mr-3 h-5 w-5 border-2 border-white rounded-full border-t-transparent"></div>
                            Processing...
                        </div>
                    ) : (
                        <div
                            className="flex items-center gap-2 justify-center group-hover:scale-105 transition-transform">
                            {getButtonText().includes("Sign in") && (
                                <div className="w-5 h-5 flex-shrink-0 flex items-center">
                                    <svg viewBox="0 0 48 48" width="22" height="22"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path fill="#EA4335"
                                              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                                        <path fill="#4285F4"
                                              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                                        <path fill="#FBBC05"
                                              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                                        <path fill="#34A853"
                                              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                                        <path fill="none" d="M0 0h48v48H0z"></path>
                                    </svg>
                                </div>
                            )}
                            <span>{getButtonText()}</span>
                            <ChevronRight className="ml-2 w-5 h-5"/>
                        </div>
                    )}
                </Button>

                {/* --- Error display --- */}
                {(submissionError || externalSubmissionError) && (
                    <div className="p-4 mt-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start">
                            <div className="mr-3 mt-0.5">
                                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd"
                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                                          clipRule="evenodd"></path>
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-red-800">{submissionError || externalSubmissionError}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* --- Privacy & Terms --- */}
            <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-100 text-sm text-gray-600 flex">
                <Shield className="w-6 h-6 mr-3 mt-2 text-blue-600 flex-shrink-0"/>
                <div>
                    <p>Your data is secure and will never be shared or sold. <u>GDPR</u> & <u>CCPA</u> Compliant.
                    </p>
                    <p>
                        By continuing, you agree to our{' '}
                        <Link to="/terms" className="text-blue-600 underline">
                            Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-blue-600 underline">
                            Privacy Policy
                        </Link>.
                    </p>
                </div>
            </div>

            {/* --- Popups --- */}
            <CheckoutErrorPopup
                isOpen={showCheckoutErrorPopup}
                onClose={() => setShowCheckoutErrorPopup(false)}
                email={userEmail}
                selectedPackage={selectedPackage}
            />

            <TermsOfUsePopup
                isOpen={showTermsPopup}
                onClose={() => setShowTermsPopup(false)}
            />

            <PrivacyPolicyPopup
                isOpen={showPrivacyPopup}
                onClose={() => setShowPrivacyPopup(false)}
            />

            <JobPreferencesPopup
                isOpen={showJobPreferencesPopup}
                onClose={() => setShowJobPreferencesPopup(false)}
                initialValue={formData.job_preferences}
                onSave={handleJobPreferencesUpdate}
            />

            {/* --- Global styles --- */}
            <style jsx global>{`
                @keyframes bounceX {
                    0%, 100% {
                        transform: translateX(0);
                    }
                    50% {
                        transform: translateX(3px);
                    }
                }

                input::placeholder,
                textarea::placeholder {
                    color: #6b7280 !important;
                    opacity: 1;
                    display: block;
                    white-space: normal;
                    transition: none;
                    font-size: 1rem !important;
                    line-height: 1.5 !important;
                    overflow: visible;
                }

                @media (max-width: 767px) {
                    .form-container h2 {
                        font-size: 23px !important;
                        line-height: 1.2 !important;
                    }
                }

                #preferences {
                    resize: none !important;
                    min-height: auto !important;
                    height: calc(1.4em * 3 + 1rem) !important;
                    font-size: 1rem !important;
                    line-height: 1.5 !important;
                    white-space: normal !important;
                    overflow-y: auto !important;
                }

                #preferences::placeholder {
                    font-size: 0.875rem !important;
                    white-space: normal !important;
                    overflow: visible !important;
                    word-wrap: break-word !important;
                    display: block !important;
                }
            `}</style>
        </div>)
    };

    return showLemonCheckoutShadow ? (
        <>
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300 pointer-events-none"/>
            {renderContent()}
        </>
    ) : (
        renderContent()
    );
}